import React, { useState } from 'react';
import { Star, Moon, Sun } from 'lucide-react';
import { SHADOW_WORK_TEMPLATES, LIGHT_WORK_TEMPLATES } from './JournalTemplates';
import { JournalTemplateType } from '../../types';

interface TemplateSelectorProps {
  selectedTemplate: JournalTemplateType;
  onSelect: (template: JournalTemplateType) => void;
}

type WorkType = 'shadow' | 'light';

export default function TemplateSelector({ selectedTemplate, onSelect }: TemplateSelectorProps) {
  const [activeTab, setActiveTab] = useState<WorkType>('shadow');
  
  // Load favorite templates from localStorage
  const [favorites, setFavorites] = React.useState<JournalTemplateType[]>(() => {
    const saved = localStorage.getItem('favoriteTemplates');
    return saved ? JSON.parse(saved) : ['brain-dump'];
  });

  // Toggle favorite status
  const toggleFavorite = (templateId: JournalTemplateType, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavorites(prev => {
      const newFavorites = prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId];
      localStorage.setItem('favoriteTemplates', JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  const templates = activeTab === 'shadow' ? SHADOW_WORK_TEMPLATES : LIGHT_WORK_TEMPLATES;

  return (
    <div className="space-y-6">
      {/* Tabs - Full width on mobile */}
      <div className="grid grid-cols-2 gap-0 border border-mint-200 rounded-lg overflow-hidden">
        <button
          onClick={() => setActiveTab('shadow')}
          className={`
            flex items-center justify-center space-x-2 px-4 py-3 text-sm sm:text-base font-medium
            transition-all duration-200
            ${activeTab === 'shadow'
              ? 'bg-midnight-800 text-white shadow-lg'
              : 'bg-white text-midnight-600 hover:bg-midnight-50'
            }
          `}
        >
          <Moon className="h-4 w-4 sm:h-5 sm:w-5" />
          <span>Shadow Work</span>
        </button>
        <button
          onClick={() => setActiveTab('light')}
          className={`
            flex items-center justify-center space-x-2 px-4 py-3 text-sm sm:text-base font-medium
            transition-all duration-200
            ${activeTab === 'light'
              ? 'bg-amber-500 text-white shadow-lg'
              : 'bg-white text-midnight-600 hover:bg-amber-50'
            }
          `}
        >
          <Sun className="h-4 w-4 sm:h-5 sm:w-5" />
          <span>Light Work</span>
        </button>
      </div>

      {/* Templates Grid - 2 columns on mobile, 3-4 on larger screens */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {templates.map((template) => (
          <div
            key={template.id}
            className={`
              relative p-3 sm:p-5 rounded-lg border text-left transition-all hover:scale-[1.02]
              ${selectedTemplate === template.id
                ? activeTab === 'shadow'
                  ? 'border-midnight-500 bg-gradient-to-br from-midnight-50 to-midnight-100/50 shadow-lg'
                  : 'border-amber-500 bg-gradient-to-br from-amber-50 to-amber-100/50 shadow-lg'
                : 'border-mint-200 hover:border-midnight-300 hover:bg-midnight-50/30'
              }
            `}
          >
            {/* Favorite Button - Smaller on mobile */}
            <div 
              role="button"
              tabIndex={0}
              onClick={(e) => toggleFavorite(template.id, e)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleFavorite(template.id, e as unknown as React.MouseEvent);
                }
              }}
              className={`
                absolute top-2 right-2 sm:top-3 sm:right-3 p-1.5 sm:p-2 rounded-full transition-all cursor-pointer
                ${favorites.includes(template.id)
                  ? 'text-amber-500 hover:text-amber-600'
                  : 'text-midnight-400 hover:text-amber-500'
                }
              `}
              title={favorites.includes(template.id) ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Star className={`h-4 w-4 sm:h-5 sm:w-5 ${favorites.includes(template.id) ? 'fill-current' : ''}`} />
            </div>

            {/* Template Content */}
            <div 
              role="button"
              tabIndex={0}
              onClick={() => onSelect(template.id)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(template.id);
                }
              }}
              className="cursor-pointer"
            >
              {/* Icon - Smaller on mobile */}
              <div className={`
                p-2 sm:p-3 rounded-lg mb-2 sm:mb-3 w-fit
                ${selectedTemplate === template.id
                  ? activeTab === 'shadow'
                    ? 'bg-gradient-to-br from-midnight-500 to-midnight-400 text-white'
                    : 'bg-gradient-to-br from-amber-500 to-marigold-500 text-white'
                  : activeTab === 'shadow'
                    ? 'bg-midnight-100 text-midnight-600'
                    : 'bg-amber-100 text-amber-600'
                }
              `}>
                {React.cloneElement(template.icon, { className: 'h-5 w-5 sm:h-6 sm:w-6' })}
              </div>

              {/* Title and Description - Adjusted font sizes for mobile */}
              <div>
                <h3 className="font-medium text-sm sm:text-base text-midnight-800 mb-0.5 sm:mb-1 line-clamp-1">
                  {template.title}
                </h3>
                <p className="text-xs sm:text-sm text-midnight-600 line-clamp-2">
                  {template.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}